import { z } from 'zod';
import { endpoint, isTimeZone } from './common';

// The signed-in person's own account.

export const account = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    // A temporary ("try it out") account: no password to give, no email, deleted after a day.
    temporary: z.boolean().optional(),
    // Whether it has a password (an account made by signing in with Google or Apple has none until one is set by a reset link), and
    // the providers it can sign in with.
    hasPassword: z.boolean().optional(),
    signInProviders: z.array(z.enum(['google', 'apple'])).optional(),
});
export type Account = z.infer<typeof account>;

// The only fields a person may change about themselves. Nothing else is accepted, so a request can't reach into
// the stored account (password hash, id) through this endpoint.
export const accountChanges = z.object({
    email: z.string().trim().email('email must be an email address').optional(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
});
export type AccountChanges = z.infer<typeof accountChanges>;

export const getAccount = endpoint({
    method: 'GET',
    path: '/account',
    response: z.object({ user: account }),
});

export const updateAccount = endpoint({
    method: 'PUT',
    path: '/account',
    body: z.object({ updatedAttributes: accountChanges }),
    response: z.object({ user: z.object({ id: z.string(), email: z.string() }) }),
});

// Email from the app (the daily list of overdue tasks). Nothing is sent unless the person turns it on and confirms the address by a
// link sent to it: "pending" is on-but-not-yet-confirmed, "on" is confirmed. Temporary accounts and servers without email set up
// cannot use it (`available` is false and `unavailableReason` says why).
export const emailNotifications = z.object({
    available: z.boolean(),
    unavailableReason: z.enum(['not_set_up', 'temporary_account']).optional(),
    overdue: z.enum(['off', 'pending', 'on']),
    email: z.string(),
});
export type EmailNotifications = z.infer<typeof emailNotifications>;

export const getEmailNotifications = endpoint({
    method: 'GET',
    path: '/account/emailNotifications',
    response: z.object({ emailNotifications }),
});

// Turning it on for an address that has not confirmed sends it a confirmation link (and asking again sends another, a few times a day);
// turning it off is immediate. `timeZone` is the browser's, so due times in the email are in the person's own.
export const updateEmailNotifications = endpoint({
    method: 'PUT',
    path: '/account/emailNotifications',
    body: z.object({
        overdue: z.boolean(),
        timeZone: z.string().max(64).refine(isTimeZone, 'timeZone must be a valid IANA time zone').optional(),
    }),
    response: z.object({ emailNotifications }),
});

// Deleting your own account, with everything it owns: its boards (and so their tasks and photos), its place on other boards, its
// invitations, notifications and email settings. It cannot be undone. A regular account must give its password again; a temporary
// ("try it out") account has none to give; an account with no password (made with Google or Apple) must have signed in within the last few minutes.
export const deleteAccount = endpoint({
    method: 'POST',
    path: '/account/delete',
    body: z.object({ password: z.string().max(200).optional() }).default({}),
    response: z.object({ deleted: z.boolean() }),
});
